import {useMemo, useState} from "react";
import {CircleHelp} from "lucide-react";
import {useCreate, useDelete, useUpdate} from "@refinedev/core";
import {useTable} from "@refinedev/react-table";
import type {AdminCmsFaqItem} from "@zaaaz/shared";
import type {ColumnDef} from "@tanstack/react-table";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {
  AdminAddButton,
  AdminAlert,
  AdminBadge,
  AdminEditModal,
  AdminField,
  AdminFormActions,
  AdminHtmlEditor,
  AdminLangTabs,
  AdminModernTable,
  AdminPageLayout,
  AdminPagination,
  AdminSectionTabs
} from "../ui";

const FAQ_CATS = ["General", "Orders", "Payments", "Gifts", "Shipping and delivery", "Returns", "Support"];
const RESOURCE = "cms-faq";
const emptyForm = {categorie: 0, numeEn: "", numeRo: "", continutEn: "", continutRo: "", ordine: 0};

export function CmsFaqRefineView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("continut");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);

  const columns = useMemo<ColumnDef<AdminCmsFaqItem>[]>(() => [
    {id: "id", accessorKey: "id", header: "ID"}
  ], []);

  const {
    refineCore: {tableQuery, setCurrentPage, pageCount, currentPage, result},
  } = useTable<AdminCmsFaqItem>({
    columns,
    refineCoreProps: {
      resource: RESOURCE,
      pagination: {pageSize: 20}
    }
  });

  const {mutate: createItem, mutation: createMutation} = useCreate();
  const {mutate: updateItem, mutation: updateMutation} = useUpdate();
  const {mutate: deleteItem} = useDelete();

  const items = result.data ?? [];
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const loadError = tableQuery.error instanceof Error ? tableQuery.error.message : "";

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }

  function openItem(item: AdminCmsFaqItem) {
    setOpenId(item.id);
    setForm({
      categorie: item.categorie,
      numeEn: item.numeEn,
      numeRo: item.numeRo,
      continutEn: item.continutEn,
      continutRo: item.continutRo,
      ordine: item.ordine
    });
    setModalOpen(true);
  }

  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }

  function saveItem() {
    if(openId === "new") {
      createItem({resource: RESOURCE, values: form}, {
        onSuccess: () => {
          adminToast.success("Question added.");
          closeModal(false);
          tableQuery.refetch();
        },
        onError: (e) => adminToast.error(e.message)
      });
      return;
    }
    if(typeof openId === "number") {
      updateItem({resource: RESOURCE, id: openId, values: form}, {
        onSuccess: () => {
          adminToast.success("Question saved.");
          closeModal(false);
          tableQuery.refetch();
        },
        onError: (e) => adminToast.error(e.message)
      });
    }
  }

  function removeItem(id: number) {
    confirm("Delete?", () => {
      deleteItem({resource: RESOURCE, id}, {
        onSuccess: () => {
          adminToast.success("Question deleted.");
          closeModal(false);
          tableQuery.refetch();
        },
        onError: (e) => adminToast.error(e.message)
      });
    });
  }

  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.continut.title}
      description="Frequently asked questions (Refine CRUD)"
      breadcrumbs={[
        {label: "Dashboard", href: "/vadmin"},
        {label: "Content", href: "/vadmin/continut"},
        {label: "FAQ"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add question" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="continut" badges={badges} />
      {loadError ? <AdminAlert variant="error">{loadError}</AdminAlert> : null}
      <AdminModernTable
        items={items}
        getId={(item) => item.id}
        getTitle={(item) => item.numeRo || item.numeEn}
        getSubtitle={(item) => item.categorieLabel}
        getMeta={(item) => <AdminBadge variant="secondary">Ord. {item.ordine}</AdminBadge>}
        getSearchText={(item) => adminSearchText(item.id, item.categorieLabel, item.numeRo, item.numeEn, item.continutRo, item.continutEn, item.ordine)}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        loading={tableQuery.isLoading}
        emptyTitle="No FAQ questions"
        emptyDescription="Add a new question."
        searchPlaceholder="Search question, category, content..."
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        icon={CircleHelp}
        title={openId === "new" ? "New question" : "Edit question"}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminField label="Category">
            <Select value={String(form.categorie)} onValueChange={(v) => setForm({...form, categorie: Number(v)})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {FAQ_CATS.map((c, i) => <SelectItem key={c} value={String(i)}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </AdminField>
          <AdminField label="Order">
            <Input type="number" value={form.ordine} onChange={(e) => setForm({...form, ordine: Number(e.target.value)})} />
          </AdminField>
          <AdminLangTabs
            ro={(
              <>
                <AdminField label="Question">
                  <Input value={form.numeRo} onChange={(e) => setForm({...form, numeRo: e.target.value})} />
                </AdminField>
                <AdminField label="Answer">
                  <AdminHtmlEditor value={form.continutRo} onChange={(html) => setForm({...form, continutRo: html})} />
                </AdminField>
              </>
            )}
            en={(
              <>
                <AdminField label="Question">
                  <Input value={form.numeEn} onChange={(e) => setForm({...form, numeEn: e.target.value})} />
                </AdminField>
                <AdminField label="Answer">
                  <AdminHtmlEditor value={form.continutEn} onChange={(html) => setForm({...form, continutEn: html})} />
                </AdminField>
              </>
            )}
          />
        </div>
      </AdminEditModal>
      <AdminPagination
        page={currentPage}
        totalPages={pageCount}
        onPrev={() => setCurrentPage(currentPage - 1)}
        onNext={() => setCurrentPage(currentPage + 1)}
      />
    </AdminPageLayout>
  );
}
